// components/Hero.js - Enhanced version with advanced wave animation
import { useRef, useState } from 'react';
interface HeroSectionProps {
    isScrolled: boolean;
    logo: string;
}

const HeroWaves:React.FC<HeroSectionProps> = ({isScrolled, logo}) => {
  const waveContainerRef = useRef<HTMLDivElement>(null);
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      try {
        const response = await fetch('https://uwcwwlvt42.execute-api.us-east-1.amazonaws.com/dev/subscribe', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email }),
        })
        
        if (response.ok) {
          setIsSubmitted(true);
          setEmail("");
          setTimeout(() => setIsSubmitted(false), 5000);
        } else {
          throw new Error('Failed to submit your email')
        }
      } catch (error) {
        console.error('Error submitting email:', error);
      }
    }
  };

  // Removed parallax effect to maintain stable positioning
  
  return (
    <section className="pt-4 pb-16 relative overflow-hidden h-[80vh] flex items-center">
      <div className="container mx-auto px-4 relative z-10">
        <div className='max-w-3xl'>
        {!isScrolled && (
            <div className="flex items-center text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight -mt-8">
              <img src={logo} alt="Logo" className="w-10 h-10 mr-2" />
              <span className='font-josefin font-medium text-[45px]'>Evovance</span>
            </div>
          )}
          <div className='mt-10'></div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">EVOLVE EVERY DAY</h1>
          <p className="text-xl mb-8">
            Unlock Your Potential With Tools Designed To Help You Grow! 
            <br />
            One Habit, One Step, One Breakthrough At A Time.
          </p>
          
          <div className="mb-12">
            <h3 className="mb-4">Get updates</h3>
            <form 
              onSubmit={handleSubmit} 
              className="flex max-w-md"
            >
              <input 
                type="email" 
                placeholder="Enter your email here" 
                className="bg-transparent border border-gray-600 rounded-l-full px-4 py-2 w-full focus:outline-none focus:border-purple-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              
              <button 
                type="submit" 
                className="bg-purple-700 text-white px-4 py-2 rounded-r-full hover:bg-purple-600 transition-colors"
              >
                GO
              </button>
              {isSubmitted && (
                <span className="text-green-500 text-xs sm:text-sm">
                  Thank you for submitting your email! We will be in touch soon.
                </span>
              )}
            </form>
          </div>
        </div>
      </div>
      
      {/* Advanced Animated Waves */}
      <div 
        className="absolute bottom-0 left-0 w-full h-[200px] sm:h-[250px] md:h-[300px] overflow-hidden z-0" 
        ref={waveContainerRef}
      >
        <svg 
          viewBox="0 0 1440 300" 
          className="absolute bottom-0 left-0 w-full h-full"
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#2196F3">
                <animate 
                  attributeName="stop-color" 
                  values="#2196F3; #00BCD4; #2196F3" 
                  dur="10s" 
                  repeatCount="indefinite" 
                />
              </stop>
              <stop offset="100%" stopColor="#FF9800">
                <animate 
                  attributeName="stop-color" 
                  values="#FF9800; #FFC107; #FF9800" 
                  dur="10s" 
                  repeatCount="indefinite" 
                />
              </stop>
            </linearGradient>
            
            <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#9C27B0">
                <animate 
                  attributeName="stop-color" 
                  values="#9C27B0; #673AB7; #9C27B0" 
                  dur="12s" 
                  repeatCount="indefinite" 
                />
              </stop>
              <stop offset="100%" stopColor="#F44336">
                <animate 
                  attributeName="stop-color" 
                  values="#F44336; #E91E63; #F44336" 
                  dur="12s" 
                  repeatCount="indefinite" 
                />
              </stop>
            </linearGradient>
            
            <linearGradient id="gradient3" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#00BCD4">
                <animate 
                  attributeName="stop-color" 
                  values="#00BCD4; #4CAF50; #00BCD4" 
                  dur="14s" 
                  repeatCount="indefinite" 
                />
              </stop>
              <stop offset="100%" stopColor="#FFC107">
                <animate 
                  attributeName="stop-color" 
                  values="#FFC107; #FF9800; #FFC107" 
                  dur="14s" 
                  repeatCount="indefinite" 
                />
              </stop>
            </linearGradient>
            
            {/* Add filters for glow effect */}
            <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="8" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
          </defs>
          
          {/* Wave paths with CSS and SMIL animations */}
          <path 
            className="wave-path-1"
            d="M0,120L48,125C96,130,192,140,288,145C384,150,480,160,576,155C672,150,768,130,864,125C960,120,1056,130,1152,135C1248,140,1344,140,1392,140L1440,140L1440,300L1392,300C1344,300,1248,300,1152,300C1056,300,960,300,864,300C768,300,672,300,576,300C480,300,384,300,288,300C192,300,96,300,48,300L0,300Z" 
            fill="url(#gradient1)"
            opacity="0.8"
            filter="url(#glow)"
          >
            {/* Additional SMIL animation for opacity pulsing */}
            <animate 
              attributeName="opacity" 
              values="0.7;0.9;0.7" 
              dur="8s" 
              repeatCount="indefinite" 
            />
          </path>
          
          <path 
            className="wave-path-2"
            d="M0,160L48,150C96,140,192,120,288,115C384,110,480,120,576,125C672,130,768,130,864,120C960,110,1056,90,1152,85C1248,80,1344,90,1392,95L1440,100L1440,300L1392,300C1344,300,1248,300,1152,300C1056,300,960,300,864,300C768,300,672,300,576,300C480,300,384,300,288,300C192,300,96,300,48,300L0,300Z" 
            fill="url(#gradient2)"
            opacity="0.6"
            filter="url(#glow)"
          >
            <animate 
              attributeName="opacity" 
              values="0.5;0.7;0.5" 
              dur="10s" 
              repeatCount="indefinite" 
            />
          </path>
          
          <path 
            className="wave-path-3"
            d="M0,80L48,90C96,100,192,120,288,125C384,130,480,120,576,110C672,100,768,80,864,80C960,80,1056,100,1152,105C1248,110,1344,100,1392,95L1440,90L1440,300L1392,300C1344,300,1248,300,1152,300C1056,300,960,300,864,300C768,300,672,300,576,300C480,300,384,300,288,300C192,300,96,300,48,300L0,300Z" 
            fill="url(#gradient3)"
            opacity="0.7"
            filter="url(#glow)"
          >
            <animate 
              attributeName="opacity" 
              values="0.6;0.8;0.6" 
              dur="12s" 
              repeatCount="indefinite" 
            />
          </path>
        </svg>
      </div>
    </section>
  );
};

export default HeroWaves;